import { formatTimecode } from '@/internationalization/format'
import type { Summary, SummarySection } from '@/types'

/**
 * Topic sections, as the UI reads them. Pure, so the rules below are testable
 * without mounting anything (this project has no DOM harness).
 */

/**
 * The summary's sections, or `[]`.
 *
 * `sections` is OPTIONAL on the type although the API always sends it, because
 * the UI and the API deploy separately: a UI served against an API that
 * predates the field would otherwise crash on `.length` of undefined, on every
 * meeting page. Empty means "render the narrative", which is exactly what every
 * summary written before topic sections renders.
 */
export function sectionsOf(summary: Pick<Summary, 'sections'> | null | undefined): SummarySection[] {
  return summary?.sections ?? []
}

/**
 * The summary's topic sections in timeline order, or `[]`.
 *
 * The API already sends them ordered, so this is a guard rather than a source
 * of order: a stored row, a fallback merge or a future writer that interleaves
 * them must not reach the screen out of sequence. STABLE, and a section with no
 * time inherits the key of the one before it, so it stays with the topic it
 * followed instead of being thrown to either end -- the rule the server applies.
 */
export function topicSections(summary: Pick<Summary, 'sections'> | null | undefined): SummarySection[] {
  const keyed = sectionsOf(summary).reduce<{ key: number; index: number; section: SummarySection }[]>(
    (acc, section, index) => {
      const key = section.timestampSeconds ?? acc[acc.length - 1]?.key ?? -1
      acc.push({ key, index, section })
      return acc
    },
    [],
  )
  return keyed.sort((a, b) => a.key - b.key || a.index - b.index).map((entry) => entry.section)
}

/**
 * The text shown at the top of the summary, ahead of the topic sections.
 *
 * With sections it is the OVERVIEW; where the summary has sections and no
 * overview (one written before overviews existed, whose narrative was then the
 * short overview) it falls back to the narrative so that summary still opens
 * with something. Without sections it is the narrative, as it always was. The
 * email never reads this: it renders `narrative` alone.
 */
export function summaryLead(
  summary: Pick<Summary, 'sections' | 'overview' | 'narrative'>,
): string {
  if (sectionsOf(summary).length === 0) return summary.narrative
  return summary.overview?.trim() || summary.narrative
}

/**
 * `6:22` / `1:06:41` for a section that has a time, `null` for one that has not.
 *
 * NULL, NOT `0:00`. A topic whose start could not be placed has no time, and
 * `0:00` would claim it opened the meeting. Zero itself is a real offset and is
 * formatted like any other, so the test is `== null` and never falsiness. The
 * caller renders the house empty value (an em dash) for the null case.
 */
export function sectionTime(seconds: number | null | undefined): string | null {
  return seconds == null ? null : formatTimecode(seconds)
}

/**
 * The plain-text lines for a section, for "Copy summary".
 *
 * `Heading (6:22)` then the body, then a blank line. A section with no time
 * carries no parenthesis at all -- pasted into an email or a ticket there is
 * nothing to show for a missing time, and a dash would read as a typo.
 */
export function sectionsCopyLines(sections: SummarySection[]): string[] {
  return sections.flatMap((section) => {
    const time = sectionTime(section.timestampSeconds)
    return [time === null ? section.heading : `${section.heading} (${time})`, section.summary, '']
  })
}
